<template>
  <d-card :class="{ 'mb-3': true, 'is-deleted': productData.deleted }">
    <d-card-body class="pb-2">
      <d-row>
        <d-col cols="4">
          <div>
            {{ productName }}<br>
            <small>
              Ilość: <span class="text-primary">{{ productData.quantity }} szt.</span> |
              Kolor: <span class="text-primary">{{ productColor }}</span>
            </small>
          </div>
          <small
            v-if="canEdit"
            class="d-block pt-2">
            <a
              class="text-danger"
              href="#delete"
              @click.prevent="deleteProduct">
              <i class="material-icons">delete</i>
              Usuń produkt
            </a>
          </small>
        </d-col>

        <d-col cols="8">
          <div class="d-flex text-right align-items-center">
            <d-col cols="4">
              <label
                v-if="(!procedureIsAssigned && availableSizes.length > 1) || !productData.size_id"
                class="d-flex text-left mb-1">
                <small>Rozmiar:</small>
              </label>

              <div
                v-if="canEdit && ((!procedureIsAssigned && availableSizes.length > 1) || !productData.size_id)"
                :class="{ 'form-control': true, 'is-select': true }">
                <v-select
                  v-model="productData.size_id"
                  placeholder="Wybierz rozmiar"
                  :options="availableSizes"
                  label="name"
                  :reduce="(size) => size.id" />
              </div>

              <div v-else-if="!canEdit && productData.size_id && (procedureIsAssigned || availableSizes.length === 1)">
                Rozmiar: <span class="text-primary">{{ productSize }}</span>
              </div>
            </d-col>

            <d-col cols="4">
              <label
                v-if="canEdit"
                class="d-flex text-left mb-1">
                <small>Polar:</small>
              </label>

              <div
                v-if="canEdit"
                :class="{ 'form-control': true, 'is-select': true }">
                <v-select
                  v-model="productData.polar_id"
                  :reduce="(polar) => polar.id"
                  placeholder="Wybierz polar"
                  label="name"
                  :options="polars" />
              </div>

              <div v-else-if="!canEdit && productData.polar_id">
                Polar: <span class="text-primary">{{ productPolar }}</span>
              </div>
            </d-col>

            <d-col cols="4">
              <label
                v-if="canEdit"
                class="d-flex text-left mb-1">
                <small>Przeszycie:</small>
              </label>

              <div
                v-if="canEdit"
                :class="{ 'form-control': true, 'is-select': true }">
                <v-select
                  v-model="productData.seam_id"
                  :reduce="(seam) => seam.id"
                  placeholder="Wybierz przeszycie"
                  label="name"
                  :options="seams" />
              </div>

              <div v-else-if="!canEdit && productData.seam_id">
                Przeszycie: <span class="text-primary">{{ productSeam }}</span>
              </div>
            </d-col>
          </div>
        </d-col>
      </d-row>

      <d-row v-if="productData.procedure_start_date">
        <hr class="w-100">
        <p class="text-center m-0 w-100">
          Wybrana procedura:
          <span class="text-primary">
            {{ procedureName }}
          </span>
          <span v-if="productData.processes.length">
              |
            Wykonano {{ numberOfFinishedProcessItems }} / {{ productData.processes.length }} procesów
          </span>
        </p>
        <hr class="w-100">

        <d-alert
          v-if="this.productData.procedure_is_assigned && !productData.processes.length"
          show
          theme="warning"
          class="w-100 text-center">
          Zapisz zamówienie aby móc edytować procesy procedury dla tego produktu
        </d-alert>

        <div class="w-100 processes-list">
          <d-card
            v-for="(process, index) of this.productData.processes"
            :key="'process-' + index"
            class="mb-3">
            <d-card-body class="p-3">
              <d-badge
                v-if="processesStates[index] === 'DONE'"
                theme="success">
                <i class="material-icons">check</i>
              </d-badge>

              <d-badge
                v-if="processesStates[index] === 'IN_PROGRESS'"
                theme="primary">
                <i class="material-icons">autorenew</i>
              </d-badge>

              <d-badge
                v-if="processesStates[index] === 'WAITING'"
                theme="secondary">
                <i class="material-icons">schedule</i>
              </d-badge>

              <d-row>
                <d-col
                  :cols="process.type === 'VERIFICATION' ? 5 : 6"
                  class="d-flex align-items-center">
                  <strong class="font-weight-bold text-dark">{{ process.name }}</strong>
                </d-col>

                <d-col
                  v-if="process.type === 'VERIFICATION'"
                  cols="2"
                  class="d-flex">
                  <d-button
                    :theme="process.status === 'IN_PROGRESS' ? 'success' : 'danger'"
                    outline
                    class="w-100"
                    :disabled="processesStates[index] === 'WAITING'"
                    @click.prevent="toggleProcessItemState(index)">
                    <template v-if="process.status === 'IN_PROGRESS'">
                      Wykonano?
                    </template>
                    <template v-else>
                      Cofnij
                    </template>
                  </d-button>
                </d-col>

                <d-col
                  :cols="process.type === 'VERIFICATION' ? 5 : 6"
                  class="d-flex align-items-center justify-content-end flex-wrap">
                  <small
                    v-if="processesStates[index] === 'DONE' && startDates[index]"
                    class="text-secondary">
                   Ukończono: <strong>{{ startDates[index] }}</strong>
                  </small>

                  <small
                    v-if="(processesStates[index] === 'IN_PROGRESS' || processesStates[index] === 'WAITING') && startDates[index]"
                    :class="processesStates[index] === 'IN_PROGRESS' ? 'text-success' : 'text-primary'">
                    Planowana data startu procesu: <strong class="pr-2">{{ startDates[index] }}</strong>
                  </small>

                  <small
                    v-if="processesStates[index] === 'IN_PROGRESS' && startDates[index]"
                    :class="processesStates[index] === 'IN_PROGRESS' ? 'text-success' : 'text-primary'">
                    Planowana data ukończenia procesu:
                    <strong v-if="process.type === 'VERIFICATION'">
                      {{ startDates[index] }}
                    </strong>
                    <strong v-else>
                      {{ calculateEndDateOfProcess(index) }}
                    </strong>
                  </small>

                  <small
                    v-if="!startDates[index]"
                    class="text-danger">
                    <i class="material-icons">warning</i>
                    Nie można na ten moment obliczyć dat dla tego procesu
                  </small>
                </d-col>
              </d-row>

              <d-row v-if="process.type !== 'VERIFICATION' && processesStates[index] !== 'DONE'">
                <d-col cols="6" class="d-flex mt-3 ml-auto">

                  <div
                    v-if="filteredWorkers[process.type].length > 1"
                    class="form-control is-select w-75">
                    <v-select
                      :ref="'process-worker-' + index"
                      placeholder="Wybierz pracownika"
                      :options="filteredWorkers[process.type]"
                      label="name"
                      v-model="selectedWorkers[index]"
                      :reduce="worker => worker.id" />
                  </div>
                  <small
                    v-else-if="filteredWorkers[process.type].length === 1"
                    class="w-75 text-right pr-3 pt-2">
                    Pracownik:
                    <span class="text-primary">
                      {{ filteredWorkers[process.type][0].name }}
                    </span>
                  </small>
                  <small
                    v-else
                    class="w-75 text-right pr-3 pt-2">
                    <span class="text-danger">
                      Brak pracownika do przypisania
                    </span>
                  </small>
                  <d-button
                    v-if="filteredWorkers[process.type].length > 0"
                    theme="success"
                    outline
                    class="w-25 ml-2"
                    @click.prevent="assignWorker(index, filteredWorkers[process.type], productData.quantity)">
                    Dodaj
                  </d-button>
                </d-col>
              </d-row>

              <d-alert
                v-if="invalidSumOfQuantity(process.items, productData.quantity)"
                theme="danger"
                show
                class="text-center my-2">
                <strong>Uwaga!</strong> Suma sztuk do wykonania jest większa niż zaplanowana ilość sztuk produktu
              </d-alert>

              <d-alert
                v-if="invalidSumOfQuantityDone(process.items, productData.quantity)"
                theme="danger"
                show
                class="text-center my-2">
                <strong>Uwaga!</strong> Ilość wykonanych sztuk jest nieprawidłowa
              </d-alert>

              <table
                v-if="process.type !== 'VERIFICATION' && process.items.length"
                class="table table-sm w-100 mt-3 mb-0">
                <thead>
                  <tr>
                    <th><small><strong>Pracownik</strong></small></th>
                    <th class="text-center" style="width: 150px;"><small><strong>Do wykonania</strong></small></th>
                    <th class="text-center" style="width: 100px;"><small><strong>Dni</strong></small></th>
                    <th class="text-center" style="width: 160px;"><small><strong>Plan. data ukończenia</strong></small></th>
                    <th class="text-center" style="width: 200px;"><small><strong>Wykonano</strong></small></th>
                    <th
                      v-if="process.type === 'SEWING'"
                      class="text-center"
                      style="width: 160px;">
                      <small><strong>Nici</strong></small>
                    </th>
                    <th class="text-center" style="width: 60px;"><small><strong>Akcje</strong></small></th>
                  </tr>
                </thead>

                <tfoot>
                  <tr>
                    <td></td>
                    <td class="text-center">
                      <small>
                        Razem:
                        <strong>{{ process.items.reduce((sum, item) => sum + parseInt(item.quantity, 10), 0) }} szt.</strong>
                      </small>
                    </td>
                    <th class="text-center"></th>
                    <th class="text-center"></th>
                    <th class="text-center">
                      <small>
                        Razem:
                        <strong>{{ process.items.reduce((sum, item) => sum + parseInt(item.quantity_done, 10), 0) }} szt.</strong>
                      </small>
                    </th>
                    <th v-if="process.type === 'SEWING'"></th>
                    <th class="text-center"></th>
                  </tr>
                </tfoot>

                <tbody>
                  <tr
                    v-for="(processItem, subindex) of process.items"
                    :class="{
                      'is-overdue': processItem.end_at && +processItem.end_at.split('-').join('') < currentDate && processItem.quantity_done < processItem.quantity
                    }"
                    :key="'process-item-' + index + '-' + subindex">
                    <td class="align-middle">
                      {{ getWorkerName(processItem.user_id) }}<br>
                      <a
                        href="#"
                        class="text-primary"
                        @click.prevent="showWorkerPerformance(processItem.user_id)">
                        <small>
                          <i class="material-icons">leaderboard</i>
                          Zobacz zadania pracownika
                        </small>
                      </a>

                      <template v-if="workersPerformance[processItem.user_id]">
                        <template v-if="workersPerformance[processItem.user_id] < (processItem.quantity / processItem.days)">
                          <br>
                          <small class="text-danger">
                            <i class="material-icons">warning</i>
                            Pracownik ma za małe moce przerobowe
                          </small>
                        </template>
                      </template>
                      <template v-else>
                        <br>
                        <small class="text-danger">
                          <i class="material-icons">warning</i>
                          Brak informacji o wydajności pracownika
                        </small>
                      </template>
                    </td>
                    <td class="align-middle">
                      <d-input-group append="szt.">
                        <d-input
                          type="number"
                          min="1"
                          :state="(errors['quantity-' + index + '-' + subindex] || invalidSumOfQuantity(process.items, productData.quantity)) ? 'invalid' : null"
                          :max="productData.quantity"
                          v-model="processItem.quantity"
                          class="text-right"
                          @input="clearError('quantity-' + index + '-' + subindex)"
                          @change="processStartDates()" />
                      </d-input-group>
                    </td>
                    <td class="align-middle">
                      <d-input
                        type="number"
                        min="1"
                        :state="errors['days-' + index + '-' + subindex] ? 'invalid' : null"
                        v-model="processItem.days"
                        class="text-center"
                        @input="clearError('days-' + index + '-' + subindex)"
                        @change="updateEndDate(index, subindex, processItem.days)" />
                    </td>
                    <td class="align-middle text-center">
                      <small v-if="processItem.end_at">
                        <span
                          v-if="processItem.end_at && +processItem.end_at.split('-').join('') < currentDate && processItem.quantity_done < processItem.quantity"
                          class="text-danger">
                          {{ processItem.end_at }}
                        </span>
                        <template v-else>
                          {{ processItem.end_at }}
                        </template>
                      </small>
                      <small v-else>
                        brak danych
                      </small>
                    </td>
                    <td class="align-middle">
                      <d-input-group append="szt.">
                        <d-input-group-addon prepend>
                          <d-btn
                            theme="success"
                            @click.prevent="processItem.quantity_done = processItem.quantity"
                            :disabled="processItem.quantity_done === processItem.quantity">
                            <i class="material-icons">check_circle</i>
                          </d-btn>
                        </d-input-group-addon>
                        <d-input
                          type="number"
                          min="1"
                          :max="productData.quantity"
                          class="text-right"
                          :state="(errors['quantity-done-' + index + '-' + subindex] || invalidSumOfQuantityDone(process.items, productData.quantity) || parseInt(processItem.quantity_done, 10) > parseInt(processItem.quantity, 10)) ? 'invalid' : null"
                          @input="clearError('quantity-done-' + index + '-' + subindex)"
                          v-model="processItem.quantity_done" />
                      </d-input-group>
                    </td>
                    <td
                      v-if="process.type === 'SEWING'"
                      class="align-middle">
                      <div
                        :class="{ 'form-control is-select w-100': true, 'is-invalid': errors['strands-' + index + '-' + subindex] }">
                        <v-select
                          placeholder="Wybierz nić"
                          :options="strands"
                          label="name"
                          @input="clearError('strands-' + index + '-' + subindex)"
                          v-model="processItem.strand_id"
                          :reduce="strand => strand.id" />
                      </div>
                    </td>
                    <td class="align-middle">
                      <d-button
                        theme="danger"
                        outline
                        @click.prevent="removeProcessItem(index, subindex)">
                        Usuń
                      </d-button>
                    </td>
                  </tr>
                </tbody>
              </table>

              <d-alert
                v-if="process.type !== 'VERIFICATION' && !process.items.length"
                show
                theme="light"
                class="text-center mt-3 mb-0">
                Wybierz pracowników, którzy zajmą się tym procesem.
              </d-alert>
            </d-card-body>
          </d-card>
        </div>
      </d-row>
    </d-card-body>

    <d-card-footer
      v-if="!productData.procedure_start_date"
      bg-theme="light"
      border-theme="secondary">
      <d-row>
        <d-col cols="4">
          <d-checkbox
            inline
            :disabled="!canEdit"
            v-model="productData.pattern_required"
            toggle
            toggle-small>
            Wymaga wykroju
          </d-checkbox>

          <d-checkbox
            v-if="productData.pattern_required"
            inline
            :disabled="!canEdit"
            v-model="productData.pattern_before_order"
            toggle
            toggle-small>
            Wykrajanie przed zamówieniem
          </d-checkbox>

          <template v-if="productData.pattern_stays">
            <br>
            <small class="text-danger">
              <strong>
                Wykrój zostaje w hafciarni
              </strong>
            </small>
          </template>
        </d-col>

        <d-col cols="8" class="text-right">
          <template v-if="!procedureIsAssigned || !productData.procedure_id">
            <div
              v-if="availableProcedures.length && productData.size_id"
              class="d-flex">
              <div
                :class="{ 'form-control': true, 'is-select': true }">
                <v-select
                  v-model="productData.procedure_id"
                  placeholder="Wybierz procedurę"
                  value="value"
                  :options="availableProcedures"
                  label="name"
                  :reduce="procedure => procedure.id" />
              </div>

              <d-button
                theme="success"
                outline
                size="sm"
                class="w-50 ml-2"
                :disabled="!productData.procedure_id"
                @click.prevent="assignProcedure">
                Przypisz procedurę
              </d-button>
            </div>

            <div v-else-if="!availableProcedures.length">
              <span class="text-danger">
                Ten produkt nie ma zdefiniowanych procedur
              </span>
            </div>

            <div v-else-if="!productData.size_id">
              <span class="text-danger">
                Aby wybrać procedurę, należy najpierw wybrać rozmiar
              </span>
            </div>
          </template>
          <template v-else>
            <div class="d-flex align-items-center">
              <div class="w-50">
                Wybrana procedura:
                <span class="text-primary">
                  {{ procedureName }}
                </span>
                <small
                  class="text-danger cursor-pointer"
                  @click.prevent="changeProcedure">
                  <i class="material-icons">edit</i>
                  Zmień
                </small>
              </div>

              <div class="w-50">
                Termin startu:
                <d-button-group size="small">
                  <d-button
                    theme="success"
                    outline
                    @click.prevent="setStartDateToToday">
                    Dziś
                  </d-button>

                  <d-button
                    theme="success"
                    outline
                    @click.prevent="selectStartDate">
                    Wyznacz
                  </d-button>
                </d-button-group>
              </div>
            </div>
          </template>
        </d-col>
      </d-row>
    </d-card-footer>
  </d-card>
</template>

<script>
import Vue from 'vue';

export default {
  name: 'order-creator-product',
  props: [
    'productData',
    'productIndex',
    'products',
    'procedures',
    'colors',
    'seams',
    'strands',
    'sizes',
    'polars',
    'workers',
    'canEdit'
  ],
  computed: {
    productApiData () {
      let foundedProduct = this.products.filter(product => product.id === this.productData.product_id);

      if (foundedProduct[0]) {
        return foundedProduct[0];
      }

      return false;
    },
    productName () {
      if (this.productApiData) {
        return this.productApiData.name;
      }

      return '---';
    },
    productsWithAllColorsAndSizes () {
      return this.products.filter(product => product.price_code === product.code).map(product => product.id);
    },
    isProductWithAllColorsAndSizes () {
      return this.productsWithAllColorsAndSizes.indexOf(this.productData.product_id) > -1;
    },
    productColor () {
      if (this.productData.color_id && this.productData.id === null) {
        let foundedColor = this.colors.filter(color => color.id === this.productData.color_id);

        if (foundedColor[0]) {
          return foundedColor[0].name;
        }

        return '???';
      }

      if (!this.productData.color_id && this.productData.id === null && this.productApiData && this.productApiData.color_name) {
        return this.productApiData.color_name;
      }

      if (this.productData && this.productData.color_name) {
        return this.productData.color_name;
      }

      return 'Brak';
    },
    productSize () {
      if (this.productData && this.productData.size_name) {
        return this.productData.size_name;
      }

      if (this.productData.size_id) {
        let foundedSize = this.sizes.filter(size => size.id === this.productData.size_id);

        if (foundedSize[0]) {
          return foundedSize[0].name;
        }
      }

      return 'Brak';
    },
    productPolar () {
      if (this.productData && this.productData.polar_name) {
        return this.productData.polar_name;
      }

      if (this.productData.polar_id) {
        let foundedPolar = this.polars.filter(size => size.id === this.productData.polar_id);

        if (foundedPolar[0]) {
          return foundedPolar[0].name;
        }
      }

      return 'Brak';
    },
    productSeam () {
      if (this.productData && this.productData.seam_name) {
        return this.productData.seam_name;
      }

      if (this.productData.seam_id) {
        let foundedSeam = this.seams.filter(size => size.id === this.productData.seam_id);

        if (foundedSeam[0]) {
          return foundedSeam[0].name;
        }
      }

      return 'Brak';
    },
    availableSizes () {
      if (this.isProductWithAllColorsAndSizes) {
        return this.sizes.map(size => ({
          id: size.id,
          name: size.name
        }));
      }

      if (this.productApiData) {
        let sizes = JSON.parse(JSON.stringify(this.productApiData.sizes));

        if (sizes.length) {
          sizes = sizes.filter(size => !!size).map(size => ({
            id: size.id,
            name: size.name
          }));
        }

        return sizes;
      }

      return [];
    },
    availableProcedures () {
      if (this.productApiData) {
        let procedures = JSON.parse(JSON.stringify(this.productApiData.procedures));

        if (procedures.length) {
          procedures = procedures.map(procedure => ({
            id: procedure.id,
            name: procedure.name
          }));
        }

        return procedures;
      }

      return [];
    },
    procedureIsAssigned () {
      return this.productData.procedure_is_assigned || this.productData.procedure_start_date;
    },
    assignedProcedureData () {
      let procedureID = this.productData.procedure_id;
      let foundedProcedure = this.procedures.filter(procedure => procedure.id === procedureID);

      if (foundedProcedure[0]) {
        return foundedProcedure[0];
      }

      return false;
    },
    procedureName () {
      if (this.assignedProcedureData) {
        return this.assignedProcedureData.name;
      }

      return '---';
    },
    startDates () {
      return this.productData.processes.map(process => process.start_at);
    },
    processesStates () {
      let states = [];
      let processes = this.productData.processes;

      for (let i = 0; i < processes.length; i++) {
        if (processes[i].type === 'VERIFICATION') {
          states[i] = processes[i].status;

          if (states[i] === 'IN_PROGRESS' && i > 0 && states[i - 1] !== 'DONE') {
            states[i] = 'WAITING';
          }
        } else {
          let quantityDone = processes[i].items.reduce((sum, item) => sum + parseInt(item.quantity_done, 10), 0);
          let isDone = quantityDone === this.productData.quantity;

          if ((i === 0 && isDone) || (isDone && states[i - 1] === 'DONE')) {
            states[i] = 'DONE';
          } else if ((!isDone && i === 0) || (!isDone && states[i - 1] === 'DONE')) {
            states[i] = 'IN_PROGRESS';
          } else {
            states[i] = 'WAITING';
          }
        }
      }

      return states;
    },
    numberOfFinishedProcessItems () {
      return this.processesStates.filter(state => state === 'DONE').length;
    },
    workersPerformance () {
      let workersPerformance = {};

      let procedureID = this.productData.procedure_id;
      let foundedProcedure = this.procedures.filter(procedure => procedure.id === procedureID);

      if (foundedProcedure[0]) {
        for (let i = 0; i < foundedProcedure[0].users.length; i++) {
          let id = foundedProcedure[0].users[i].id;
          let capability = foundedProcedure[0].users[i].capability;
          workersPerformance[id] = capability;
        }
      }

      return workersPerformance;
    },
    currentDate () {
      let currentDate = new Date();
      currentDate = [currentDate.getFullYear(), currentDate.getMonth() + 1, currentDate.getDate()].map(n => n < 10 ? '0' + n : n).join('');
      return parseInt(currentDate, 10);
    },
    filteredWorkers () {
      let foundedProcedure = this.procedures.filter(procedure => procedure.id === this.productData.procedure_id);
      let availableWorkers = foundedProcedure[0] ? foundedProcedure[0].users.map(user => user.id) : [];

      return {
        'PATTERN': this.workers.filter(worker => worker.is_pattern),
        'SEWING': this.workers.filter(worker => worker.is_seamstress && availableWorkers.indexOf(worker.id) > -1)
      }
    }
  },
  watch: {
    'productData.size_id': function (newValue, oldValue) {
      if (newValue !== oldValue) {
        Vue.set(this.productData, 'procedure_id', null);
      }
    }
  },
  data () {
    return {
      errors: {},
      selectedWorkers: []
    };
  },
  mounted () {
    this.processStartDates();
    this.$bus.$on('order-product-procedure-start', this.setStartDate);
  },
  methods: {
    deleteProduct () {
      Vue.swal({
        title: 'Czy na pewno chcesz usunąć ten produkt z zamówienia?',
        text: 'Tej operacji nie można cofnąć.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Tak, usuń',
        cancelButtonText: 'Anuluj'
      }).then((result) => {
        if (result.value) {
          this.$bus.$emit('order-form-remove-product', this.productIndex);
        }
      });
    },
    assignProcedure () {
      Vue.set(this.productData, 'procedure_is_assigned', true);
    },
    selectStartDate () {
      this.$bus.$emit('select-date-popup-show', {
        title: 'Wyznacz termin startu procedury',
        responseEventName: 'order-product-procedure-start',
        referrerID: this.productIndex
      });
    },
    setStartDateToToday () {
      let currentDate = new Date();
      let formattedDate = [currentDate.getFullYear(), currentDate.getMonth() + 1, currentDate.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
      this.productData.procedure_start_date = formattedDate;
    },
    setStartDate (response) {
      if (response.referrerID === this.productIndex) {
        this.productData.procedure_start_date = response.date;
      }
    },
    changeProcedure () {
      this.productData.procedure_is_assigned = false;
    },
    assignWorker (processIndex, availableWorkers, maxQty) {
      if (!this.selectedWorkers[processIndex] && availableWorkers.length > 1) {
        Vue.swal({
          title: 'Nie wybrano pracownika',
          text: 'Musisz wybrać pracownika, aby przypisać go do wybranego procesu.',
          icon: 'error',
          confirmButtonText: 'OK'
        });

        return;
      }

      let userID = this.selectedWorkers[processIndex];
      let userQty = 1;

      if (availableWorkers.length === 1) {
        userID = availableWorkers[0].id;

        if (this.productData.processes[processIndex].type === 'PATTERN') {
          userQty = maxQty;
        }
      }

      let assignedWorkers = this.productData.processes[processIndex].items.map(item => item.user_id);

      if (assignedWorkers.indexOf(userID) > -1) {
        Vue.swal({
          title: 'Ten pracownik jest już przypisany',
          text: 'Wybrany pracownik jest już przypisany do tego procesu. Wybierz innego pracownika.',
          icon: 'error',
          confirmButtonText: 'OK'
        });

        return;
      }

      Vue.set(this.selectedWorkers, processIndex, null);
      this.addWorkerToProcess(processIndex, userID, userQty);
    },
    addWorkerToProcess (processIndex, userID, userQty = 1) {
      this.processStartDates();

      this.productData.processes[processIndex].items.push({
        user_id: userID,
        quantity: userQty,
        quantity_done: 0,
        days: 1,
        end_at: this.calculateEndDate(processIndex, 1),
        strand_id: this.productData.default_strand_id ? this.productData.default_strand_id : null
      });

      this.processStartDates();
    },
    toggleProcessItemState (index) {
      let status = this.productData.processes[index].status;
      this.productData.processes[index].status = status === 'DONE' ? 'IN_PROGRESS' : 'DONE';
      this.processStartDates();

      // Fix status of the further process items
      if (status !== 'DONE') {
        return;
      }

      // Find next processes with type of verification and change their state
      for (let i = index + 1; i < this.productData.processes.length; i++) {
        if (this.productData.processes[i].type === 'VERIFICATION' && this.productData.processes[i].status === 'DONE') {
          this.productData.processes[i].status = 'IN_PROGRESS';
        }
      }
    },
    getWorkerName (userID) {
      let foundedWorker = this.workers.filter(worker => worker.id === userID);

      if (foundedWorker[0]) {
        return foundedWorker[0].name;
      }

      return '---';
    },
    calculateEndDate (processIndex, days) {
      let startDate = this.startDates[processIndex];

      if (!startDate) {
        return false;
      }

      startDate = startDate.split('-').map(n => parseInt(n, 10));
      let startDateObject = new Date(startDate[0], startDate[1] - 1, startDate[2], 0, 0, 0, 0);
      days = parseInt(days, 10);
      days -= 1;
      startDateObject.setDate(startDateObject.getDate() + days);

      return [startDateObject.getFullYear(), startDateObject.getMonth() + 1, startDateObject.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    updateEndDate (processIndex, processItemIndex, days) {
      this.productData.processes[processIndex].items[processItemIndex].end_at = this.calculateEndDate(processIndex, days);
      this.processStartDates();
    },
    calculateEndDateOfProcess(processIndex) {
      let startDate = this.startDates[processIndex];

      if (!startDate || !this.productData.processes[processIndex].items.length) {
        return 'nieznana';
      }

      startDate = startDate.split('-').map(n => parseInt(n, 10));
      let days = this.productData.processes[processIndex].items.slice().map(item => item.days);
      days.sort((itemA, itemB) => itemB - itemA);
      days = days[0];
      days -= 1;
      let startDateObject = new Date(startDate[0], startDate[1] - 1, startDate[2], 0, 0, 0, 0);
      startDateObject.setDate(startDateObject.getDate() + parseInt(days, 10));

      return [startDateObject.getFullYear(), startDateObject.getMonth() + 1, startDateObject.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    removeProcessItem (processIndex, processItemIndex) {
      Vue.swal({
        title: 'Czy na pewno chcesz usunąć tego pracownika z procesu?',
        text: 'Tej operacji nie można cofnąć.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Tak, usuń',
        cancelButtonText: 'Anuluj'
      }).then((result) => {
        if (result.value) {
          this.productData.processes[processIndex].items.splice(processItemIndex, 1);
          this.processStartDates();
        }
      });

      // uncheck further verification processes
      for (let i = processIndex; i < this.productData.processes.length; i++) {
        if (this.productData.processes[i].type === 'VERIFICATION') {
          Vue.set(this.productData.processes[i], 'status', 'IN_PROGRESS');
        }
      }
    },
    processStartDates () {
      for (let i = 0; i < this.productData.processes.length; i++) {
        if (i === 0) {
          Vue.set(this.productData.processes[0], 'start_at', this.productData.procedure_start_date);
          continue;
        }

        if (!this.productData.processes[i - 1]) {
          Vue.set(this.productData.processes[i], 'start_at', null); // we set start date to false if previous start date is unknown
          continue;
        }

        let offset = this.findPreviousNonVerificationProcess(this.productData.processes[i].id);

        if (offset === -1) {
          Vue.set(this.productData.processes[i], 'start_at', this.productData.procedure_start_date);
          continue;
        }

        let items = JSON.parse(JSON.stringify(this.productData.processes[offset].items));
        items.sort((itemA, itemB) => itemB.days - itemA.days);
        let quantityToDo = items.reduce((sum, item) => sum + parseInt(item.quantity, 10), 0);

        if (quantityToDo !== this.productData.quantity) {
          Vue.set(this.productData.processes[i], 'start_at', null);
          continue;
        }

        Vue.set(this.productData.processes[i], 'start_at', items[0].end_at);

        let processItems = this.productData.processes[i].items;

        if (processItems.length) {
          for (let j = 0; j < processItems.length; j++) {
            Vue.set(processItems[j], 'end_at', this.calculateEndDate(i, processItems[j].days));
          }
        }
      }

      let newDates = this.productData.processes.map(process => process.start_at);
      let emptyDatePosition = newDates.indexOf(null);

      if (emptyDatePosition > -1) {
        for (let i = emptyDatePosition; i < this.productData.processes.length; i++) {
          Vue.set(this.productData.processes[i], 'start_at', null);
        }
      }
    },
    findPreviousNonVerificationProcess (processID) {
      let allProcessesIDs = this.productData.processes.map(process => process.id);
      let nonVerificationProcesses = this.productData.processes.filter(process => process.type !== 'VERIFICATION' || process.id === processID);
      nonVerificationProcesses = nonVerificationProcesses.map(process => process.id);
      let currentProcessIndex = nonVerificationProcesses.indexOf(processID);

      if (currentProcessIndex === 0) {
        return -1; // -1 means "use procedure start date"
      }

      let previousNonVerificationProcessID = nonVerificationProcesses[currentProcessIndex - 1];
      return allProcessesIDs.indexOf(previousNonVerificationProcessID);
    },
    invalidSumOfQuantity (processItems, expectedQuantity) {
      let sumTodo = processItems.reduce((sum, item) => sum + parseInt(item.quantity, 10), 0);
      return sumTodo > expectedQuantity;
    },
    invalidSumOfQuantityDone (processItems, expectedQuantity) {
      let sumDone = processItems.reduce((sum, item) => sum + parseInt(item.quantity_done, 10), 0);
      let invalidItemSums = processItems.filter(item => parseInt(item.quantity_done, 10) > parseInt(item.quantity, 10));
      return sumDone > expectedQuantity || invalidItemSums.length;
    },
    setErrors (errors) {
      Vue.set(this, 'errors', errors);
    },
    clearError (key) {
      if (this.errors[key]) {
        delete this.errors[key];
      }
    },
    showWorkerPerformance (workerID) {
      let foundedWorker = this.workers.filter(worker => worker.id === workerID);

      if (foundedWorker[0]) {
        let workerPerformance = foundedWorker[0].occupancies;
        let workerName = foundedWorker[0].name;

        this.$bus.$emit('order-display-worker-performance-popup', {
          performance: workerPerformance,
          name: workerName
        });
      }
    }
  },
  beforeDestroy () {
    this.$bus.$off('order-product-procedure-start', this.setStartDate);
  }
}
</script>

<style lang="scss">
.is-deleted {
  pointer-events: none;

  & > * {
    opacity: .5;
  }

  &:before {
    background: #d22;
    border-radius: 10px 10px 0 0;
    color: #fff;
    content: "Produkt usunięty";
    height: 24px;
    font-size: 12px;
    line-height: 25px;
    position: absolute;
    text-align: center;
    width: 100%;
  }
}

.order-creator {
  .processes-list {
    .card {
      margin-left: 80px;
    }

    .card-body {
      position: relative;

      .alert {
        border-radius: 5px!important;
        font-size: 12px;
        padding: .25rem .5rem;
      }
    }

    .card-body > .badge {
      align-items: center;
      border-radius: 50%;
      display: flex;
      left: -70px;
      justify-content: center;
      height: 40px;
      padding: 0;
      position: absolute;
      top: calc(50% - 20px);
      width: 40px;

      i {
        font-size: 28px;
        position: relative;
        top: 0;
      }
    }
  }

  tr.is-overdue {
    background: rgba(255, 50, 50, .075);
  }
}
</style>
